'use client';

import { useState, useMemo } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { YoutubeIcon, Survey3DIcon } from '@/shared/components/icons';
import { Listing } from '../types';
import { ImagePlaceholder } from '@/shared/components/ui/ImagePlaceholder';
import Link from 'next/link';
import { extractYoutubeVideoId } from '../utils';

interface Props {
    listing: Listing;
}

export function ListingMedia(props: Props) {
    const { listing } = props;
    const href = `/inzerat/${props.listing.id}/${props.listing.slug}?gallery=open`;
    const hasTour = Boolean((listing as any).virtualTourUrl);

    const youtubeEmbedUrl = useMemo(() => {
        if (!listing.youtubeVideoUrl) return null;

        const videoId = extractYoutubeVideoId(listing.youtubeVideoUrl);

        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }, [listing.youtubeVideoUrl]);
    const hasYoutube = Boolean(youtubeEmbedUrl);

    const mediaNav = useMemo(
        () => [
            ...(youtubeEmbedUrl
                ? [{ key: 'youtube', icon: <YoutubeIcon className="size-[20px]" />, text: 'Video' }]
                : []),
            ...(hasTour
                ? [
                      {
                          key: 'survey',
                          icon: <Survey3DIcon className="size-[20px]" />,
                          text: '3D Prehliadka',
                      },
                  ]
                : []),
        ],
        [youtubeEmbedUrl, hasTour],
    );

    const [activeView, setActiveView] = useState<string>(mediaNav[0]?.key ?? 'photo');

    const tourUrl: string | null = (listing as any).virtualTourUrl ?? null;
    const fallbackPhotoUrl: string | null = useMemo(() => {
        return listing?.photo?.url ?? null;
    }, [listing.photo?.url]);

    return (
        <div className={hasTour || hasYoutube ? 'bg-[#000]' : 'p-[8px] !pb-[0]'}>
            {/* Main view */}
            <div
                className={cn(
                    'relative w-full overflow-hidden min-h-[250px] h-[30vh] flex flex-col xl:h-[45vh]',
                    'sm:min-h-[400px] md:min-h-[450px] lg:min-h-[450px] xl:min-h-[550px] 2xl:min-h-[600px] 3xl:min-h-[700px]',
                )}
            >
                <div className="flex-1 min-h-0">
                    {/* YouTube */}
                    {activeView === 'youtube' && youtubeEmbedUrl && (
                        <iframe
                            src={youtubeEmbedUrl}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}

                    {/* 3D tour */}
                    {activeView === 'survey' && tourUrl && (
                        <iframe src={tourUrl} className="w-full h-full" allowFullScreen />
                    )}

                    {/* Fallback - first photo or placeholder */}
                    {!youtubeEmbedUrl && !tourUrl && (
                        <Link href={href} className="block h-full min-h-0 overflow-hidden">
                            <div className="grid h-full min-h-0 grid-cols-[65fr_35fr] gap-[8px] overflow-hidden xl:gap-[10px]">
                                <div className="relative min-h-0 min-w-0 overflow-hidden">
                                    {fallbackPhotoUrl ? (
                                        <img
                                            src={fallbackPhotoUrl}
                                            alt="photo"
                                            className="absolute inset-0 h-full w-full object-cover rounded-[8px] xl:gap-[10px]"
                                        />
                                    ) : (
                                        <ImagePlaceholder className="h-full w-full text-white" />
                                    )}
                                </div>

                                <div className="grid h-full min-h-0 grid-rows-2 gap-[8px] overflow-hidden xl:gap-[10px]">
                                    <div className="relative min-h-0 overflow-hidden">
                                        {props.listing?.gallery?.[1] ? (
                                            <img
                                                src={props.listing.gallery[1].url}
                                                alt="photo"
                                                className="absolute inset-0 h-full w-full object-cover rounded-[8px] xl:rounded-[12px]"
                                            />
                                        ) : (
                                            <ImagePlaceholder className="h-full w-full text-white" />
                                        )}
                                    </div>

                                    <div className="relative min-h-0 overflow-hidden">
                                        {props.listing?.gallery?.[2] ? (
                                            <img
                                                src={props.listing.gallery[2].url}
                                                alt="photo"
                                                className="absolute inset-0 h-full w-full object-cover rounded-[8px] xl:rounded-[12px]"
                                            />
                                        ) : (
                                            <ImagePlaceholder className="h-full w-full text-white rounded-[8px] xl:rounded-[12px]" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    )}
                </div>
            </div>
            {/* Nav - only when both media types are present */}
            {mediaNav.length > 1 && (
                <div className="cust-container">
                    <div className="flex justify-center px-[20px] py-[3px] bg-[#000] gap-[10px]">
                        {mediaNav.map((item, idx) => (
                            <a
                                key={idx}
                                className={cn(
                                    'flex flex-1 items-center justify-center gap-[5px] text-white text-[12px] font-[600] text-center',
                                    'py-[10px] rounded-[30px] transition-all duration-300 ease-in-out cursor-pointer',
                                    activeView === item.key && 'bg-[#eac410] text-black',
                                )}
                                onClick={() => setActiveView(item.key)}
                            >
                                {item.icon}
                                {item.text}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
